import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Clock, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { PublicShell, SignupCta, useSeo } from "@/pages/glossary/PublicShell";

const catLabel = (cats, key) => (cats.find((c) => c.key === key) || {}).label || "Article";
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "");

function ArticleCard({ a, cats, featured }) {
  return (
    <Link to={`/articles/${a.slug}`} data-testid={`article-card-${a.slug}`}
      className={`group block rounded-2xl border border-kid-line bg-white overflow-hidden hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(25,55,80,.10)] transition-all ${featured ? "sm:flex" : ""}`}>
      <div className={`bg-kid-skytint/50 overflow-hidden ${featured ? "sm:w-1/2 aspect-[16/10]" : "aspect-[16/9]"}`}>
        {a.header_image_url && (
          <img src={a.header_image_url} alt={a.header_image_alt || ""} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        )}
      </div>
      <div className={`p-5 ${featured ? "sm:w-1/2 sm:p-7 flex flex-col justify-center" : ""}`}>
        <span className="text-xs font-semibold uppercase tracking-wide text-kid-tealcta">{catLabel(cats, a.category_id)}</span>
        <h3 className={`font-display font-extrabold text-kid-navy mt-1.5 leading-snug ${featured ? "text-2xl" : "text-lg"}`}>{a.title}</h3>
        <p className="text-sm text-kid-muted mt-2 line-clamp-2">{a.excerpt || a.subtitle}</p>
        <div className="flex items-center gap-3 text-xs text-kid-muted mt-3">
          <span>{fmtDate(a.published_at)}</span>
          {a.read_time_minutes ? <span className="inline-flex items-center gap-1"><Clock size={12} /> {a.read_time_minutes} min read</span> : null}
        </div>
      </div>
    </Link>
  );
}

export default function ArticlesIndex() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, categories: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("search") || "");
  const cat = params.get("category") || "";
  const page = parseInt(params.get("page") || "1", 10);

  useSeo({
    title: "Articles for Canadian Families Raising Neurodivergent Children | KidPath",
    description: "Explore parent-friendly guides about autism supports, funding, school planning, therapy navigation, and resources for Canadian families.",
    jsonLd: { "@context": "https://schema.org", "@type": "CollectionPage", name: "KidPath Articles", url: "https://kidpath.ca/articles" },
  });

  const load = useCallback(async () => {
    setLoading(true);
    const sp = new URLSearchParams();
    if (cat) sp.set("category", cat);
    if (params.get("search")) sp.set("search", params.get("search"));
    sp.set("page", String(page));
    const { data } = await api.get(`/articles?${sp.toString()}`);
    setData(data);
    setLoading(false);
  }, [cat, page, params]);

  useEffect(() => { load(); }, [load]);

  const setParam = (k, v) => { const n = new URLSearchParams(params); v ? n.set(k, v) : n.delete(k); n.delete("page"); setParams(n); };
  const submitSearch = (e) => { e.preventDefault(); setParam("search", q); };

  const cats = data.categories || [];
  const featured = page === 1 && !cat && !params.get("search") ? data.items[0] : null;
  const rest = featured ? data.items.slice(1) : data.items;
  const totalPages = Math.ceil(data.total / (data.limit || 12));

  return (
    <PublicShell>
      <div className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-12 sm:py-16" data-testid="articles-index">
        <span className="text-sm font-semibold uppercase tracking-wide text-kid-tealcta">KidPath Articles</span>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-kid-navy mt-2 max-w-3xl leading-tight">
          Helpful Guides for Canadian Families Raising Neurodivergent Children
        </h1>
        <p className="text-kid-muted mt-4 max-w-2xl">
          Find clear, inclusive, parent-friendly articles to help you understand funding options, school supports, therapy pathways, and practical next steps.
        </p>

        <form onSubmit={submitSearch} className="mt-7 relative max-w-md" data-testid="articles-search-form">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-kid-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…" data-testid="articles-search-input"
            className="w-full h-12 rounded-full border border-kid-line pl-11 pr-4 text-sm focus:border-kid-teal outline-none" />
        </form>

        <div className="flex flex-wrap gap-2 mt-6" data-testid="articles-category-filters">
          <button onClick={() => setParam("category", "")} data-testid="article-cat-all"
            className={`text-sm font-semibold rounded-full px-4 py-2 border transition-colors ${!cat ? "bg-kid-tealcta text-white border-kid-tealcta" : "border-kid-line text-kid-navy hover:border-kid-teal"}`}>All</button>
          {cats.map((c) => (
            <button key={c.key} onClick={() => setParam("category", c.key)} data-testid={`article-cat-${c.key}`}
              className={`text-sm font-semibold rounded-full px-4 py-2 border transition-colors ${cat === c.key ? "bg-kid-tealcta text-white border-kid-tealcta" : "border-kid-line text-kid-navy hover:border-kid-teal"}`}>{c.label}</button>
          ))}
        </div>

        {loading ? (
          <p className="text-kid-muted mt-12" data-testid="articles-loading">Loading articles…</p>
        ) : data.items.length === 0 ? (
          <div className="mt-12 text-center py-16 border border-dashed border-kid-line rounded-2xl" data-testid="articles-empty">
            <p className="text-kid-navy font-semibold">No articles yet</p>
            <p className="text-kid-muted text-sm mt-1">New parent-friendly guides are on the way — check back soon.</p>
          </div>
        ) : (
          <>
            {featured && <div className="mt-10"><ArticleCard a={featured} cats={cats} featured /></div>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="articles-grid">
              {rest.map((a) => <ArticleCard key={a.id} a={a} cats={cats} />)}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10" data-testid="articles-pagination">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button key={p} onClick={() => setParam("page", String(p))}
                    className={`w-10 h-10 rounded-full text-sm font-semibold ${p === page ? "bg-kid-tealcta text-white" : "border border-kid-line text-kid-navy"}`}>{p}</button>
                ))}
              </div>
            )}
          </>
        )}

        <div className="mt-14"><SignupCta /></div>
      </div>
    </PublicShell>
  );
}
